import React, { useState, useEffect } from 'react';
import { Link } from 'gatsby';
import PropTypes from 'prop-types';
import styled, { css } from 'styled-components';
import { navLinks } from '@config';
import Logo from './logo';
import Menu from './menu';

const StyledHeader = styled.header`
  position: fixed;
  top: 0;
  left: 0;
  z-index: 30;
  width: 100%;
  height: var(--nav-height);
  display: flex;
  align-items: center;
  transition: var(--transition);

  ${({ $solid }) =>
    $solid
      ? css`
          background-color: rgba(255, 255, 255, 0.92);
          backdrop-filter: blur(14px);
          border-bottom: 1px solid var(--line);
          box-shadow: 0 18px 40px -34px rgba(11, 18, 32, 0.55);
          color: var(--text);
        `
      : css`
          background-color: transparent;
          color: var(--on-dark);
        `}
`;

const StyledNav = styled.nav`
  ${({ theme }) => theme.mixins.container};
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 24px;
`;

const StyledLogoLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  color: inherit;

  &:hover,
  &:focus {
    color: inherit;
    opacity: 0.85;
  }
`;

const StyledLinks = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;

  @media (max-width: 860px) {
    display: none;
  }

  ul {
    ${({ theme }) => theme.mixins.resetList};
    display: flex;
    align-items: center;
    gap: 4px;

    li a {
      display: inline-block;
      padding: 10px 14px;
      border-radius: 999px;
      color: inherit;
      font-size: var(--fz-sm);
      font-weight: 500;
      transition: var(--transition);

      &:hover,
      &:focus {
        color: inherit;
        background: ${({ $solid }) => ($solid ? 'var(--paper-tint)' : 'rgba(255, 255, 255, 0.1)')};
      }

      &.active {
        color: ${({ $solid }) => ($solid ? 'var(--blue)' : '#ffffff')};
      }
    }
  }
`;

const StyledCTA = styled(Link)`
  ${({ theme }) => theme.mixins.primaryButton};
  margin-left: 12px;
`;

const Nav = ({ isHome, pathname = '/' }) => {
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.pageYOffset > 24);
    handleScroll();
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const solid = !isHome || scrolled;

  return (
    <StyledHeader $solid={solid}>
      <StyledNav>
        <StyledLogoLink to="/" aria-label="SMART-KVADRAT — на главную">
          <Logo size={38} />
        </StyledLogoLink>

        <StyledLinks $solid={solid}>
          <ul>
            {navLinks.map(({ url, name }) => (
              <li key={url}>
                <Link
                  to={url}
                  className={pathname === url ? 'active' : undefined}
                  activeClassName="active">
                  {name}
                </Link>
              </li>
            ))}
          </ul>
          <StyledCTA to="/contacts">Обсудить проект</StyledCTA>
        </StyledLinks>

        <Menu solid={solid} />
      </StyledNav>
    </StyledHeader>
  );
};

Nav.propTypes = {
  isHome: PropTypes.bool,
  pathname: PropTypes.string,
};

export default Nav;
